import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useRef, useState } from "react"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx"
import {
  Ban,
  Calendar,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Download,
  FileCheck,
  FileExclamationPoint,
  FileStack,
  FileText,
  Info,
  Languages,
  MapPin,
  MessageCircleMore,
  Paperclip,
  Receipt,
  Shapes,
  User,
  UserPen,
} from "lucide-react"
import { cn, formatBytes, formatDate, getStoredLocale } from "@/lib/utils.ts"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.tsx"
import { Separator } from "@/components/ui/separator.tsx"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet.tsx"
import { Status, StatusBadge } from "@/components/StatusBadge.tsx"
import { Comments } from "@/components/Comments.tsx"
import { CommentInput } from "@/components/CommentInput.tsx"
import {
  Item,
  ItemContent,
  ItemHeader,
  ItemTitle,
} from "@/components/ui/item.tsx"
import { useMutation, useQuery } from "@tanstack/react-query"
import { pitchesApi } from "@/api/pitches.api.ts"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { Button } from "@/components/ui/button.tsx"
import { queryClient } from "@/lib/query-client"
import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from "@/components/ui/attachment.tsx"
import { API_URL } from "@/api/client.ts"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx"
import { ErrorCard } from "@/components/ErrorCard.tsx"
import { CommissioningOffer } from "@/components/CommissioningOffer.tsx"
import { useUser } from "@/contexts/UserContext.tsx"
import { useTranslation } from "react-i18next"
import type { RichEditorHandle } from "@/components/RichEditor.tsx"

export const Route = createFileRoute("/_protected/pitch/$publicId")({
  ssr: false,
  component: RouteComponent,
})

const DetailsContent = ({ pitch }: { pitch: any }) => {
  const { t } = useTranslation()
  const locale = getStoredLocale()
  const submitPitch = useMutation({
    mutationFn: pitchesApi.submit,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pitches/getByPublicId", pitch.publicId],
      })
    },
  })
  const onClickSubmit = () => {
    submitPitch.mutate(pitch.id)
  }
  return (
    <div
      dir={locale === "ar" ? "rtl" : "ltr"}
      className={locale === "ar" ? "font-arabic" : ""}
    >
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <FileExclamationPoint size={14} /> {t("details.status")}:
        </span>
        <StatusBadge status={pitch.lastVisibleStatus} />
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <Calendar size={14} /> {t("details.posted")}:
        </span>
        <span>{formatDate(pitch.creationTime)}</span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <User size={14} /> {t("details.journalist")}:
        </span>
        <span>{pitch.authorName}</span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <Languages size={14} /> {t("details.lang")}:
        </span>
        <span>
          {pitch.languages
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <MapPin size={14} /> {t("details.loc")}:
        </span>
        <span>
          {pitch.locations
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <FileStack size={14} /> {t("details.format")}:
        </span>
        <span>
          {pitch.formats
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <Shapes size={14} /> {t("details.themes")}:
        </span>
        <span>
          {pitch.themes
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      {pitch.editorName && (
        <div className="flex items-center gap-1">
          <span className="flex min-w-24 flex-row items-center gap-1">
            <UserPen size={14} /> {t("details.editor")}:
          </span>
          <span>{pitch.editorName}</span>
        </div>
      )}
      {pitch.lastVisibleStatus === Status.SAVED && (
        <div className="mt-4 w-full">
          <Button className="w-full" onClick={onClickSubmit}>
            {t("details.submit")}
          </Button>
        </div>
      )}
    </div>
  )
}

function RouteComponent() {
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()
  const [pitch, setPitch] = useState<any>(undefined)
  const { locale } = useUser()
  const { t } = useTranslation()
  const isArabic = locale === "ar"

  const { publicId } = useParams({ from: "/_protected/pitch/$publicId" })

  const [comment, setComment] = useState("")
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const { data, isLoading, error } = useQuery({
    queryKey: ["pitches/getByPublicId", publicId],
    queryFn: () => pitchesApi.getByPublicId(publicId),
    structuralSharing: false,
  })
  const {
    data: commissioningOffer,
    isLoading: isOfferLoading,
    error: isOfferError,
  } = useQuery({
    queryKey: ["pitches/getCommissioningOfferById", publicId],
    queryFn: () => pitchesApi.getCommissioningOfferByPitchId(data?.result?.id),
    enabled: !!data?.result?.commissioningOfferPublicId,
    structuralSharing: false,
  })

  const approveFinalVersion = useMutation({
    mutationFn: pitchesApi.approveFinalVersion,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pitches/getByPublicId", publicId],
      })
    },
  })
  const addComment = useMutation({
    mutationFn: pitchesApi.addComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pitches/getByPublicId", publicId],
      })
    },
  })

  const onApproveFinalVersion = () => {
    approveFinalVersion.mutate({ pitchId: pitch.id })
  }

  const handleSubmit = (commentType: 0 | 1 = 0, audioUrl?: string) => {
    if (commentType == 0) {
      setIsSubmittingComment(true)
    }
    // post comment
    addComment.mutate(
      {
        pitchId: pitch.id,
        commentText: commentType === 1 && audioUrl ? audioUrl : comment,
        commentType,
      },
      {
        onSuccess: () => {
          if (commentType == 0) {
            setIsSubmittingComment(false)
            setComment("")
            commentsRef.current?.reset()
          }
        },
      }
    )
  }

  useEffect(() => {
    setTitle(t("sidebar.pitches"))
  }, [setTitle])

  useEffect(() => {
    if (data && (data as any).result) {
      setPitch((data as any).result)
    }
  }, [data])

  const commentsRef = useRef<RichEditorHandle>(null)

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="mb-4 h-8 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="mb-4 h-5 w-1/2" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-1/4" />
      </div>
    )
  }
  if (error) {
    return <ErrorCard backLink="/pitches" />
  }

  return pitch ? (
    <div className="w-full">
      <header className="mb-4">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-secondary-foreground">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 pe-2 text-secondary-foreground"
              onClick={() => navigate({ to: "/pitches" })}
            >
              {isArabic ? <ChevronRight /> : <ChevronLeft />}
              <span className="not-md:hidden">{t("common.back")}</span>
            </Button>
            <Separator orientation="vertical" className="mr-2 ml-2" />
            <Calendar size={14} /> {formatDate(pitch.creationTime)}
          </div>
          <StatusBadge status={pitch.lastVisibleStatus} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <h1 className="mt-4 mb-4 font-heading text-2xl">{pitch.title}</h1>
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger className="flex items-center">
                <Info className="text-secondary-foreground" />
              </SheetTrigger>
              <SheetContent side="bottom" dir={locale === "ar" ? "rtl" : "ltr"}>
                <SheetHeader>
                  <SheetTitle
                    className={locale === "ar" ? "font-arabic" : "font-sans"}
                  >
                    {" "}
                    {t("details.title")}:
                  </SheetTitle>
                </SheetHeader>
                <SheetDescription className="p-4 pt-0">
                  <DetailsContent pitch={pitch} />
                </SheetDescription>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <div className="flex h-full w-full max-w-full flex-row gap-2">
        <div className="w-full lg:pe-3">
          <Tabs
            defaultValue={
              pitch.commissioningOfferPublicId
                ? "commissioningOffer"
                : pitch.hasFinalVersion
                  ? "finalVersion"
                  : "discussion"
            }
          >
            <div className="max-w-full overflow-x-auto">
              <TabsList className="mb-4">
                <TabsTrigger value="discussion">
                  <MessageCircleMore />
                  {t("pitch.discussion")}
                </TabsTrigger>
                {pitch.hasFinalVersion && pitch.finalVersion && (
                  <TabsTrigger value="finalVersion">
                    <FileCheck />
                    {t("pitch.finalVersion")}
                  </TabsTrigger>
                )}
                {pitch.commissioningOfferPublicId && (
                  <TabsTrigger value="commissioningOffer">
                    <Receipt />
                    {t("pitch.commissioningOffer")}
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
            <TabsContent value="discussion">
              <div
                className="pitch-container w-full wrap-break-word hyphens-auto"
                style={{ wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{ __html: pitch.descriptionHtml }}
              />
              {pitch.attachments.length > 0 && (
                <Card className="mt-8 mb-4 bg-muted/50">
                  <CardHeader>
                    <CardTitle
                      className={cn(
                        "flex items-center gap-1",
                        locale === "ar" && "font-arabic"
                      )}
                    >
                      <Paperclip size={20} /> {t("pitch.attach")}
                    </CardTitle>
                  </CardHeader>
                  <Separator />
                  <CardContent className="flex flex-row items-start justify-start not-md:flex-col">
                    {pitch.attachments.map((attach: any) => (
                      <div
                        className="m-1 flex gap-2 not-md:w-full"
                        key={`attachment-${attach.id}`}
                      >
                        <Dialog>
                          <Attachment className="w-64 not-md:w-full">
                            {attach.attachmentType === "Image" && (
                              <AttachmentMedia variant="image">
                                <img src={`${API_URL}${attach.url}`} alt="" />
                              </AttachmentMedia>
                            )}
                            {attach.attachmentType !== "Image" && (
                              <AttachmentMedia variant="icon">
                                <FileText />
                              </AttachmentMedia>
                            )}
                            <AttachmentContent>
                              <AttachmentTitle>
                                {attach.fileName}
                              </AttachmentTitle>
                              <AttachmentDescription>
                                {attach.attachmentType} -{" "}
                                {formatBytes(attach.fileSizeBytes)}
                              </AttachmentDescription>
                            </AttachmentContent>
                            <DialogTrigger
                              render={
                                <AttachmentTrigger
                                  aria-label={`Preview ${attach.fileName}`}
                                />
                              }
                            />
                          </Attachment>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle>{attach.fileName}</DialogTitle>
                              <DialogDescription>
                                {attach.attachmentType === "Image" && (
                                  <img
                                    className="w-full"
                                    src={`${API_URL}${attach.url}`}
                                    alt=""
                                  />
                                )}
                                {attach.attachmentType !== "Image" && (
                                  <div className="m-4 flex items-center justify-center">
                                    <Button
                                      size="lg"
                                      variant="outline"
                                      onClick={() =>
                                        window.open(
                                          `${API_URL}${attach.url}`,
                                          "_blank",
                                          "noreferrer noopener"
                                        )
                                      }
                                    >
                                      <Download /> Download {attach.fileName}
                                    </Button>
                                  </div>
                                )}
                              </DialogDescription>
                            </DialogHeader>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              <Card className="mt-8 mb-4 bg-muted/50">
                <CardHeader>
                  <CardTitle
                    className={cn(
                      "flex items-center gap-1",
                      locale === "ar" && "font-arabic"
                    )}
                  >
                    <MessageCircleMore size={20} /> {t("comments.title")}{" "}
                    {pitch.comments.length > 0
                      ? `(${pitch.comments.length})`
                      : ""}
                  </CardTitle>
                </CardHeader>

                {pitch.comments.length > 0 && (
                  <>
                    <Separator />
                    <CardContent>
                      <Comments comments={pitch.comments} />
                    </CardContent>
                  </>
                )}

                <CardFooter>
                  {!pitch.isCommentsClosed ? (
                    <CommentInput
                      value={comment}
                      onChange={setComment}
                      onSubmit={handleSubmit}
                      isSubmitting={isSubmittingComment}
                      ref={commentsRef}
                    />
                  ) : (
                    <p className="flex items-center gap-2">
                      <Ban className="text-primary" /> {t("comments.closed")}
                    </p>
                  )}
                </CardFooter>
              </Card>
            </TabsContent>
            {pitch.hasFinalVersion && pitch.finalVersion && (
              <TabsContent value="finalVersion">
                <div className="mb-2 flex justify-end">
                  {pitch.finalVersion.isFinalVersionApprovedByJournalist ? (
                    <span className="flex items-center gap-1 text-emerald-400">
                      <CheckCheck /> {t("pitch.approvedFinal")}
                    </span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onApproveFinalVersion}
                    >
                      <Check /> {t("pitch.approveFinal")}
                    </Button>
                  )}
                </div>
                <h1 className="font-heading text-lg">
                  {pitch.finalVersion.title}
                </h1>
                <Separator className="mt-4 mb-4" />
                <div
                  className="pitch-container w-full wrap-break-word hyphens-auto"
                  style={{ wordBreak: "break-word" }}
                  dangerouslySetInnerHTML={{
                    __html: pitch.finalVersion.descriptionHtml,
                  }}
                />
              </TabsContent>
            )}
            {pitch.commissioningOfferPublicId && commissioningOffer && (
              <TabsContent value="commissioningOffer">
                <CommissioningOffer
                  offer={commissioningOffer.result.active}
                  isLoading={isOfferLoading}
                  publicId={publicId}
                />
              </TabsContent>
            )}
          </Tabs>
        </div>
        <div className="w-md not-lg:hidden">
          <Item variant="muted">
            <ItemHeader className="mb-2">
              <ItemTitle>{t("details.title")}</ItemTitle>
            </ItemHeader>
            <ItemContent className="text-secondary-foreground">
              <DetailsContent pitch={pitch} />
            </ItemContent>
          </Item>
        </div>
      </div>
    </div>
  ) : null
}
